import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useAuth } from './AuthContext';
import { useFamily } from './FamilyContext';
import {
  subscribeToFeedingGroups,
  createFeedingGroup as svcCreateFeedingGroup,
  updateFeedingGroup as svcUpdateFeedingGroup,
  softDeleteFeedingGroup as svcSoftDeleteFeedingGroup,
  addDispenser as svcAddDispenser,
  removeDispenser as svcRemoveDispenser,
  renameDispenser as svcRenameDispenser,
  resizeDispenser as svcResizeDispenser,
} from '../services/feedingGroupService';

export const FeedingGroupsContext = createContext(null);

/**
 * Provider de grupos de alimentación del hogar activo.
 * Se monta dentro de AppShell, entre FamilyProvider y PetsProvider
 * (PetsProvider lo necesita para resolver datos de stock por pet).
 *
 * Estado:
 *  - groups: array de grupos activos (no soft-deleted)
 *  - status: 'idle' | 'loading' | 'ready' | 'error'
 *  - error: AppError | null
 */
export const FeedingGroupsProvider = ({ children }) => {
  const { profile } = useAuth();
  const { family } = useFamily();
  const familyId = family?.id ?? null;

  const [groups, setGroups] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!familyId) {
      setGroups([]);
      setStatus('idle');
      return;
    }

    setStatus('loading');
    const unsubscribe = subscribeToFeedingGroups(
      familyId,
      (nextGroups) => {
        const sorted = [...nextGroups].sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.createdAt?.toMillis?.() ?? 0;
          return aTime - bTime; // Más viejo primero — orden estable de creación
        });
        setGroups(sorted);
        setStatus('ready');
        setError(null);
      },
      (err) => {
        setError(err);
        setStatus('error');
      }
    );

    return unsubscribe;
  }, [familyId]);

  // ===== Mutations =====

  const createFeedingGroup = useCallback(
    async (input) => {
      if (!profile?.uid || !familyId) throw new Error('Sin sesión o sin hogar');
      return svcCreateFeedingGroup({ ...input, uid: profile.uid, familyId });
    },
    [profile?.uid, familyId]
  );

  const updateFeedingGroup = useCallback(
    async (groupId, updates) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcUpdateFeedingGroup({ uid: profile.uid, groupId, updates });
    },
    [profile?.uid]
  );

  const softDeleteFeedingGroup = useCallback(
    async (groupId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcSoftDeleteFeedingGroup({ uid: profile.uid, groupId });
    },
    [profile?.uid]
  );

  // ===== Dispensers individuales (Stage 2B) =====

  const addDispenser = useCallback(
    async (feedingGroupId, { name, capacityGr, stockGr } = {}) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcAddDispenser({ uid: profile.uid, feedingGroupId, name, capacityGr, stockGr });
    },
    [profile?.uid]
  );

  const removeDispenser = useCallback(
    async (feedingGroupId, dispenserId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcRemoveDispenser({ uid: profile.uid, feedingGroupId, dispenserId });
    },
    [profile?.uid]
  );

  const renameDispenser = useCallback(
    async (feedingGroupId, dispenserId, name) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcRenameDispenser({ uid: profile.uid, feedingGroupId, dispenserId, name });
    },
    [profile?.uid]
  );

  const resizeDispenser = useCallback(
    async (feedingGroupId, dispenserId, capacityGr) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcResizeDispenser({ uid: profile.uid, feedingGroupId, dispenserId, capacityGr });
    },
    [profile?.uid]
  );

  const getGroupById = useCallback(
    (groupId) => groups.find((g) => g.id === groupId) ?? null,
    [groups]
  );

  const value = useMemo(
    () => ({
      groups,
      status,
      error,
      isLoading: status === 'loading',
      isEmpty: status === 'ready' && groups.length === 0,
      getGroupById,
      createFeedingGroup,
      updateFeedingGroup,
      softDeleteFeedingGroup,
      addDispenser,
      removeDispenser,
      renameDispenser,
      resizeDispenser,
    }),
    [
      groups,
      status,
      error,
      getGroupById,
      createFeedingGroup,
      updateFeedingGroup,
      softDeleteFeedingGroup,
      addDispenser,
      removeDispenser,
      renameDispenser,
      resizeDispenser,
    ]
  );

  return (
    <FeedingGroupsContext.Provider value={value}>
      {children}
    </FeedingGroupsContext.Provider>
  );
};

export const useFeedingGroups = () => {
  const ctx = useContext(FeedingGroupsContext);
  if (!ctx) {
    throw new Error('useFeedingGroups must be used within a FeedingGroupsProvider');
  }
  return ctx;
};
